import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AIIcon, useAuthController } from "@rebasepro/app";
import { getFieldId } from "@rebasepro/cms";
import { isPropertyBuilder, stripCollectionPath } from "@rebasepro/common";
import { getValueInPath } from "@rebasepro/utils";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, SendIcon, Separator, TextareaAutosize, Typography, XIcon, cls, fieldBackgroundMixin, focusedDisabled, iconSize } from "@rebasepro/ui";
/**
* ## No credentials cross this boundary
*
* The old client sent the tenant's Rebase JWT as `Authorization: Basic <jwt>`
* plus a hardcoded `fcms-…` key compiled into the published package. Both were
* wrong in the same way: a self-hosted backend signs its tokens with its own
* secret, so no external service can verify one — sending it only handed a live
* credential to a third party that had no use for it.
*
* These requests are anonymous. The service bounds cost by rate limit and daily
* ceiling rather than by identity, and reports through {@link fetchAiStatus}
* when it can no longer serve — which is what keeps the UI from offering an
* action that is going to fail.
*/
function endpointOf(endpoint, path) {
	return (endpoint ?? "https://app.rebase.pro/api/functions/ai").replace(/\/+$/, "") + path;
}
/** Not global: `exec` must not carry `lastIndex` between buffer reads. */
var SSE_SEPARATOR = /\r?\n\r?\n/;
/**
* Parse an SSE body incrementally.
*
* The framing this replaces split each chunk on the literal `"&$# "` and
* `JSON.parse`d the pieces, which corrupted itself the moment a delimiter
* straddled two reads — and network reads land wherever they land. Buffering
* until a blank line is the fix, and it is also just what SSE specifies.
*/
async function* readServerSentEvents(response) {
	const reader = response.body?.getReader();
	if (!reader) throw new Error("The AI service returned no response body");
	const decoder = new TextDecoder();
	let buffer = "";
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		let match = SSE_SEPARATOR.exec(buffer);
		while (match) {
			const raw = buffer.slice(0, match.index);
			buffer = buffer.slice(match.index + match[0].length);
			const parsed = parseEventBlock(raw);
			if (parsed) yield parsed;
			match = SSE_SEPARATOR.exec(buffer);
		}
	}
}
function parseEventBlock(block) {
	let event = "message";
	const dataLines = [];
	for (const line of block.split(/\r?\n/)) {
		if (line.startsWith(":")) continue;
		const separator = line.indexOf(":");
		const field = separator === -1 ? line : line.slice(0, separator);
		const rawValue = separator === -1 ? "" : line.slice(separator + 1);
		const value = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue;
		if (field === "event") event = value;
		else if (field === "data") dataLines.push(value);
	}
	if (dataLines.length === 0) return void 0;
	return {
		event,
		data: dataLines.join("\n")
	};
}
/** Pull a message out of the control plane's `{ error: { message } }` envelope. */
async function errorFrom(response, fallback) {
	try {
		const message = (await response.json())?.error?.message;
		if (typeof message === "string" && message) return new Error(message);
	} catch {}
	return new Error(fallback);
}
/**
* Ask the service whether it can serve a request at all.
*
* The plugin gates every affordance on this. A missing provider key, an
* exhausted daily quota or an unreachable host all resolve to `available:
* false`, and the Autofill button is simply not rendered — rather than
* rendered, clicked, and failed.
*/
async function fetchAiStatus(props) {
	const response = await fetch(endpointOf(props.endpoint, "/status"), {
		method: "GET",
		signal: props.signal
	});
	if (!response.ok) return { available: false };
	const body = await response.json();
	return {
		available: Boolean(body?.available),
		model: typeof body?.model === "string" ? body.model : void 0,
		features: Array.isArray(body?.features) ? body.features : void 0
	};
}
/** One in-flight or settled probe per endpoint, for the life of the page. */
var statusProbes = /* @__PURE__ */ new Map();
/**
* {@link fetchAiStatus}, asked once per endpoint per session.
*
* The provider is form-scoped, so the uncached call meant one request to the
* host every time any record was opened — a beacon on an install that may never
* click Autofill, and enough traffic from one NAT'd office to spend the host's
* per-IP rate limit on nothing, which reads back as `available: false` and makes
* the button flicker in and out for everyone behind it.
*
* Availability changes on the order of a deploy or a daily quota reset, not of a
* form open, so a session-long answer is the right resolution. Failures resolve
* to `available: false` and are cached like any other answer — retrying per form
* open is the behaviour this replaces.
*/
function fetchAiStatusCached(props) {
	const key = endpointOf(props.endpoint, "/status");
	const existing = statusProbes.get(key);
	if (existing) return existing;
	const probe = fetchAiStatus({ endpoint: props.endpoint }).catch(() => ({ available: false }));
	statusProbes.set(key, probe);
	return probe;
}
/**
* Fill a record, streaming each field as the service writes it.
*
* `onDelta` fires with more text for a field still being written; `onValue`
* fires once a field is complete and carries its final, correctly typed value.
* A caller that implements only `onValue` still ends up with the right record —
* the deltas exist so a long text field fills in visibly instead of appearing
* all at once.
*/
async function autofillStream(props) {
	const response = await fetch(endpointOf(props.endpoint, "/autofill"), {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(props.request),
		signal: props.signal
	});
	if (!response.ok) throw await errorFrom(response, "The AI service could not complete this request.");
	let result = { suggestions: {} };
	let done = false;
	let delivered = 0;
	let discarded = 0;
	for await (const { event, data } of readServerSentEvents(response)) {
		let payload;
		try {
			payload = JSON.parse(data);
		} catch {
			discarded++;
			continue;
		}
		if (event === "suggestion_delta") {
			delivered++;
			props.onDelta(payload.key, payload.text);
		} else if (event === "suggestion") {
			delivered++;
			props.onValue(payload.key, payload.value);
		} else if (event === "done") {
			done = true;
			result = {
				suggestions: payload.suggestions ?? {},
				usage: payload.usage
			};
		} else if (event === "error") throw new Error(payload.message ?? "The AI service reported an error.");
	}
	if (!done) throw new Error("The connection to the AI service ended before it finished.");
	if (discarded > 0 && delivered === 0) throw new Error("The AI service's response could not be read.");
	return result;
}
/** Inline continuation for the rich-text editor. Streams plain text. */
async function autocompleteStream(props) {
	const response = await fetch(endpointOf(props.endpoint, "/autocomplete"), {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			textBefore: props.textBefore ?? "",
			textAfter: props.textAfter ?? ""
		}),
		signal: props.signal
	});
	if (!response.ok) throw await errorFrom(response, "The AI service could not complete this request.");
	let text = "";
	for await (const { event, data } of readServerSentEvents(response)) {
		let payload;
		try {
			payload = JSON.parse(data);
		} catch {
			continue;
		}
		if (event === "error") throw new Error(payload?.message ?? "The AI service reported an error.");
		if (event === "delta" && typeof payload?.text === "string") {
			text += payload.text;
			props.onDelta(payload.text);
		}
	}
	return text;
}
/**
* Sample prompts for the Autofill menu.
*
* Failure is deliberately not thrown: the menu has built-in prompts to fall
* back on, and an empty suggestion list is a far better outcome than an error
* toast for something nobody asked for.
*/
async function fetchPromptSuggestions(props) {
	try {
		const response = await fetch(endpointOf(props.endpoint, "/prompts"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				entityName: props.entityName,
				entityDescription: props.entityDescription,
				input: props.input
			}),
			signal: props.signal
		});
		if (!response.ok) return { prompts: [] };
		const body = await response.json();
		return { prompts: (Array.isArray(body?.prompts) ? body.prompts : []).filter((p) => typeof p === "string").map((prompt) => ({
			prompt,
			type: "sample"
		})) };
	} catch {
		return { prompts: [] };
	}
}
//#endregion
//#region src/utils/properties.ts
function getSimplifiedProperties(properties, values, path = "") {
	if (!properties) return {};
	return Object.entries(properties).map(([key, property]) => {
		if (isPropertyBuilder(property)) return {};
		const fullKey = path ? `${path}.${key}` : key;
		return getSimplifiedProperty(property, fullKey, getValueInPath(values, fullKey));
	}).reduce((a, b) => ({
		...a,
		...b
	}), {});
}
function getSimpleProperty(property) {
	const fieldId = getFieldId(property);
	if (!fieldId) {
		console.error("No fieldId found for property", property);
		throw new Error("Field id not found");
	}
	return {
		name: property.name,
		description: property.description,
		type: property.type,
		fieldConfigId: fieldId,
		enum: "enum" in property && property.enum ? getSimpleEnumValues(property.enum) : void 0,
		disabled: Boolean(property.admin?.disabled || property.admin?.readOnly)
	};
}
function getSimplifiedProperty(property, path, value) {
	if (isPropertyBuilder(property)) return {};
	if (property.type === "array") {
		if (property.of && !Array.isArray(property.of) && !isPropertyBuilder(property.of)) {
			const arrayParentProperty = {
				name: property.name,
				description: property.description,
				type: property.type,
				fieldConfigId: "repeat",
				disabled: Boolean(property.admin?.disabled || property.admin?.readOnly),
				of: getSimpleProperty(property.of)
			};
			return { [path]: arrayParentProperty };
		} else if (property.oneOf) {
			const arrayParentProperty = {
				name: property.name,
				description: property.description,
				type: property.type,
				fieldConfigId: "block",
				disabled: Boolean(property.admin?.disabled || property.admin?.readOnly),
				oneOf: {
					typeField: property.oneOf.typeField,
					valueField: property.oneOf.valueField,
					properties: Object.entries(property.oneOf.properties).map(([key, prop]) => ({ [key]: getSimpleProperty(prop) })).reduce((a, b) => ({
						...a,
						...b
					}), {})
				}
			};
			if (!Array.isArray(value)) return { [path]: arrayParentProperty };
			return value.map((v, i) => {
				if (v == null) return {};
				const typeKey = property.oneOf.typeField ?? "type";
				const oneOfType = v[typeKey];
				const valueKey = property.oneOf.valueField ?? "value";
				const oneOfValue = v[valueKey];
				const childProperty = property.oneOf.properties[oneOfType];
				if (childProperty === void 0) {
					console.error(`No property found for type ${oneOfType}`, property.oneOf.properties);
					return {};
				}
				const simplifiedProperty = getSimplifiedProperty(childProperty, `${path}.${i}.${valueKey}`, oneOfValue);
				return {
					[`${path}.${i}.${typeKey}`]: oneOfType,
					...simplifiedProperty
				};
			}).reduce((a, b) => ({
				...a,
				...b
			}), { [path]: arrayParentProperty });
		}
	} else if (property.type === "map") {
		if (property.properties) {
			const mapProperties = Object.entries(property.properties).map(([key, childProperty]) => {
				return getSimplifiedProperty(childProperty, key, value && typeof value === "object" ? value[key] : void 0);
			}).map((o) => attachPathToKeys(o, path)).reduce((a, b) => ({
				...a,
				...b
			}), {});
			if (Object.keys(mapProperties).length === 0) return {};
			const mapParentProperty = {
				name: property.name,
				description: property.description,
				type: property.type,
				fieldConfigId: "group",
				disabled: Boolean(property.admin?.disabled || property.admin?.readOnly)
			};
			return {
				[path]: mapParentProperty,
				...mapProperties
			};
		}
	} else {
		if (!getFieldId(property)) {
			console.warn(`No fieldId found for property ${path} with type ${property.type}`);
			return {};
		}
		return { [path]: getSimpleProperty(property) };
	}
	return {};
}
function attachPathToKeys(obj, path = "") {
	return Object.entries(obj).map(([key, value]) => {
		return { [path ? `${path}.${key}` : key]: value };
	}).reduce((a, b) => ({
		...a,
		...b
	}), {});
}
function getSimpleEnumValues(enumValues) {
	if (Array.isArray(enumValues)) return enumValues.map((v) => String(v.id));
	if (typeof enumValues === "object") return Object.keys(enumValues);
	throw Error("getSimpleEnumValues: Invalid enumValues");
}
//#endregion
//#region src/utils/values.ts
/**
* Flatten a record onto the dotted paths the property map uses.
*
* The two halves of an autofill request have to be keyed the same way: the
* service decides a field is empty by looking up `values[key]` for every `key`
* in `properties`, so a value filed under a key the property map has never
* heard of is a value the service cannot see.
*
* Only plain objects are containers. This used to recurse into anything
* `typeof value === "object"`, which is both an array and a `Date` — so
* `tags: ["a", "b"]` was sent as `tags.0`/`tags.1` while the property map still
* called it `tags`, and a `Date` disappeared entirely (`Object.entries(date)` is
* `[]`). Both then read as empty on the far side and came back in the review
* pre-ticked to replace a value the record already had. `getSimplifiedProperties`
* names an array by its own path and never descends into one, so neither does
* this.
*/
function flatMapEntityValues(values, path = "") {
	if (!values) return {};
	return Object.entries(values).flatMap(([key, value]) => {
		const currentPath = path ? `${path}.${key}` : key;
		if (isPlainObject(value)) return flatMapEntityValues(value, currentPath);
		else return { [currentPath]: value };
	}).reduce((acc, curr) => ({
		...acc,
		...curr
	}), {});
}
/**
* A container, as opposed to a leaf value.
*
* Arrays, dates, files and every other class instance are values in their own
* right — a map property is the only thing whose children are separate fields.
*/
function isPlainObject(value) {
	if (value === null || typeof value !== "object") return false;
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
}
/**
* Drop the values of properties the panel will not let anyone edit.
*
* A `readOnly` or `disabled` property is already excluded from what the service
* may fill, but the values map was built from the whole record, and the prompt
* includes every value it is given as context. So a field marked read-only
* because a backend hook owns it — an internal note, a customer id — was still
* being transmitted and pasted into the prompt. The collection config lives
* here, so this is the honest place to decide it: disabled means neither
* fillable nor context.
*
* Prefixes match too: a disabled map takes its children with it.
*/
function omitDisabledValues(values, properties) {
	const disabled = Object.entries(properties ?? {}).filter(([, property]) => property && typeof property === "object" && property.disabled).map(([key]) => key);
	if (disabled.length === 0) return values;
	return Object.fromEntries(Object.entries(values).filter(([key]) => !disabled.some((prefix) => key === prefix || key.startsWith(`${prefix}.`))));
}
//#endregion
//#region src/editor/useEditorAIController.tsx
/**
* Inline continuation for the rich-text editor's slash command.
*
* No token is threaded through any more. The previous version demanded a
* Firebase ID token and threw `"Firebase token is required"` when it could not
* get one — in a Rebase app there is no such thing, and the token it actually
* sent was a Rebase JWT the receiving service had no way to verify. The hosted
* service authenticates nobody; see `src/api.ts`.
*/
function useEditorAIController({ endpoint } = {}) {
	return React.useMemo(() => ({ autocomplete: (textBefore, textAfter, onUpdate) => autocompleteStream({
		endpoint,
		textBefore,
		textAfter,
		onDelta: onUpdate
	}) }), [endpoint]);
}
//#endregion
//#region src/components/DataEnhancementControllerProvider.tsx
var DataEnhancementControllerContext = React.createContext(null);
var useDataEnhancementController = () => useContext(DataEnhancementControllerContext);
function getPropertyFromKey(properties, propertyKey) {
	if (propertyKey in properties) return properties[propertyKey];
	const split = propertyKey.split(".");
	if (split.length === 1) return void 0;
	return getPropertyFromKey(properties, split.slice(0, -1).join("."));
}
/**
* Convert a value off the wire into what the form field expects.
*
* Only dates need converting: the service answers ISO-8601 strings because JSON
* has no date type, and handing a date field a string stores the wrong type
* without complaining. Everything else — strings, numbers, booleans, arrays of
* scalars — is already the shape the field wants, which is the point of having
* the service constrain its answer to a schema derived from these properties.
*/
function coerceToProperty(value, property) {
	if (property?.type === "date" && typeof value === "string") {
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? void 0 : date;
	}
	return value;
}
function DataEnhancementControllerProvider({ getConfigForPath, children, endpoint, path, collection, formContext }) {
	const [allowedHere, setAllowedHere] = useState(false);
	const [serviceAvailable, setServiceAvailable] = useState(false);
	const [review, setReview] = useState(null);
	const properties = useMemo(() => getSimplifiedProperties(collection.properties, formContext?.values ?? {}), [collection.properties, formContext?.values]);
	/**
	* Read inside the streaming callbacks, which outlive the render that
	* started the run.
	*
	* The operator is free to keep typing while the model works — nothing here
	* writes to the form — so the callbacks must not close over a stale
	* property map from whichever render happened to kick the run off.
	*/
	const propertiesRef = useRef(properties);
	propertiesRef.current = properties;
	const user = useAuthController()?.user ?? null;
	useEffect(() => {
		if (!getConfigForPath) {
			setAllowedHere(true);
			return;
		}
		setAllowedHere(Boolean(getConfigForPath({
			path,
			collection,
			user
		})));
	}, [
		getConfigForPath,
		path,
		collection,
		user
	]);
	/**
	* The service's own availability.
	*
	* Nothing renders until this comes back true. An unreachable host, an
	* unconfigured provider key or an exhausted daily quota all land here, and
	* all of them mean the same thing to the operator: no Autofill button,
	* rather than a button that fails when clicked.
	*
	* Asked through the session cache: this provider is form-scoped, so an
	* uncached probe is one request to the host per record opened, by an install
	* that may never use the feature. The probe is shared rather than aborted on
	* unmount — cancelling it would cancel it for whatever else is waiting on the
	* same answer — so unmounting only stops this component from reading it.
	*/
	useEffect(() => {
		if (!allowedHere) return;
		let cancelled = false;
		fetchAiStatusCached({ endpoint }).then((status) => {
			if (!cancelled) setServiceAvailable(status.available);
		});
		return () => {
			cancelled = true;
		};
	}, [allowedHere, endpoint]);
	const enabled = allowedHere && serviceAvailable;
	/** Add or update one row in the review, preserving arrival order. */
	const upsertField = useCallback((key, update) => {
		setReview((current) => {
			if (!current) return current;
			const index = current.fields.findIndex((f) => f.key === key);
			const next = update(index === -1 ? void 0 : current.fields[index]);
			const fields = index === -1 ? [...current.fields, next] : current.fields.map((f, i) => i === index ? next : f);
			return {
				...current,
				fields
			};
		});
	}, []);
	const generate = useCallback(async (params) => {
		const currentProperties = propertiesRef.current;
		const flatValues = omitDisabledValues(flatMapEntityValues(params.values ?? {}), currentProperties);
		setReview({
			status: "generating",
			fields: [],
			instructions: params.instructions
		});
		const labelFor = (key) => currentProperties[key]?.name ?? key;
		try {
			await autofillStream({
				endpoint,
				request: {
					entityName: collection.singularName ?? collection.name,
					entityDescription: collection.description,
					values: flatValues,
					properties: currentProperties,
					propertyKey: params.propertyKey,
					propertyInstructions: params.propertyInstructions,
					instructions: params.instructions
				},
				onDelta: (key, text) => {
					upsertField(key, (existing) => existing ? {
						...existing,
						proposed: String(existing.proposed ?? "") + text
					} : {
						key,
						label: labelFor(key),
						currentValue: getValueInPath(params.values, key),
						proposed: text,
						pending: true,
						selected: true
					});
				},
				onValue: (key, value) => {
					const coerced = coerceToProperty(value, getPropertyFromKey(currentProperties, key));
					upsertField(key, (existing) => ({
						key,
						label: existing?.label ?? labelFor(key),
						currentValue: existing?.currentValue ?? getValueInPath(params.values, key),
						proposed: coerced,
						pending: false,
						selected: existing?.selected ?? true
					}));
				}
			});
			setReview((current) => current && {
				...current,
				status: "ready",
				fields: current.fields.filter((f) => !f.pending)
			});
		} catch (e) {
			const message = e instanceof Error ? e.message : "Autofill could not be completed";
			setReview((current) => current && {
				...current,
				status: "failed",
				error: message,
				fields: current.fields.filter((f) => !f.pending)
			});
		}
	}, [
		collection,
		endpoint,
		upsertField
	]);
	const toggleField = useCallback((key) => {
		setReview((current) => current && {
			...current,
			fields: current.fields.map((f) => f.key === key ? {
				...f,
				selected: !f.selected
			} : f)
		});
	}, []);
	const toggleAll = useCallback((selected) => {
		setReview((current) => current && {
			...current,
			fields: current.fields.map((f) => ({
				...f,
				selected
			}))
		});
	}, []);
	const dismissReview = useCallback(() => setReview(null), []);
	const applyReview = useCallback(() => {
		setReview((current) => {
			if (!current) return null;
			for (const field of current.fields) {
				if (!field.selected || field.pending) continue;
				if (field.proposed === void 0 || field.proposed === null) continue;
				formContext?.setFieldValue(field.key, field.proposed);
			}
			return null;
		});
	}, [formContext]);
	const editorAIController = useEditorAIController({ endpoint });
	const getSamplePrompts = useCallback((entityName, input) => fetchPromptSuggestions({
		endpoint,
		entityName,
		entityDescription: collection.description,
		input
	}), [endpoint, collection.description]);
	const dataEnhancementController = useMemo(() => ({
		enabled,
		review,
		generate,
		toggleField,
		toggleAll,
		applyReview,
		dismissReview,
		getSamplePrompts,
		editorAIController
	}), [
		enabled,
		review,
		generate,
		toggleField,
		toggleAll,
		applyReview,
		dismissReview,
		getSamplePrompts,
		editorAIController
	]);
	return /* @__PURE__ */ jsx(DataEnhancementControllerContext.Provider, {
		value: dataEnhancementController,
		children
	});
}
//#endregion
//#region src/components/AutofillReviewDialog.tsx
/**
* The review step.
*
* Autofill used to write generated text into the live form as it streamed —
* fields mutating under the cursor, half-written sentences that looked like
* bugs, and a pile of heuristics deciding whether each token should append to
* or replace what the operator had already typed. Getting the old value back
* meant retyping it.
*
* So the generated values land here instead. Streaming still happens, and is
* still worth having — rows appear and fill in as the model works, so a long
* run shows progress — but it happens in a surface that owns nothing. The
* record changes on **Apply**, once, for the rows still ticked.
*/
function AutofillReviewDialog() {
	const controller = useDataEnhancementController();
	const review = controller?.review;
	if (!review) return null;
	const generating = review.status === "generating";
	const applicable = review.fields.filter((f) => !f.pending && f.selected);
	const allSelected = review.fields.length > 0 && review.fields.every((f) => f.selected);
	return /* @__PURE__ */ jsxs(Dialog, {
		open: true,
		maxWidth: "2xl",
		onOpenChange: (open) => {
			if (!open) controller.dismissReview();
		},
		children: [
			/* @__PURE__ */ jsx(DialogTitle, {
				variant: "subtitle1",
				gutterBottom: false,
				children: "Review autofill"
			}),
			/* @__PURE__ */ jsxs(DialogContent, {
				className: "flex flex-col gap-2",
				children: [
					review.instructions && /* @__PURE__ */ jsxs(Typography, {
						variant: "body2",
						color: "secondary",
						className: "italic",
						children: [
							"“",
							review.instructions,
							"”"
						]
					}),
					review.fields.length > 1 && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs("label", {
						className: "flex items-center gap-3 py-1 cursor-pointer select-none",
						children: [/* @__PURE__ */ jsx(Checkbox, {
							checked: allSelected,
							size: "small",
							onCheckedChange: () => controller.toggleAll(!allSelected)
						}), /* @__PURE__ */ jsx(Typography, {
							variant: "label",
							component: "span",
							color: "secondary",
							children: allSelected ? "Deselect all" : "Select all"
						})]
					}), /* @__PURE__ */ jsx(Separator, {
						orientation: "horizontal",
						className: "my-0"
					})] }),
					/* @__PURE__ */ jsx("div", {
						className: "flex flex-col divide-y divide-surface-accent-100 dark:divide-surface-accent-800",
						children: review.fields.map((field) => /* @__PURE__ */ jsx(ProposedFieldRow, {
							field,
							onToggle: () => controller.toggleField(field.key)
						}, field.key))
					}),
					generating && /* @__PURE__ */ jsxs("div", {
						className: "flex items-center gap-3 py-4 text-text-secondary dark:text-text-secondary-dark",
						children: [/* @__PURE__ */ jsx(CircularProgress, { size: "smallest" }), /* @__PURE__ */ jsx(Typography, {
							variant: "body2",
							color: "secondary",
							children: review.fields.length === 0 ? "Thinking…" : "Writing the remaining fields…"
						})]
					}),
					review.status === "failed" && /* @__PURE__ */ jsxs(Typography, {
						variant: "body2",
						className: "py-2 text-red-600 dark:text-red-400",
						children: [review.error, review.fields.length > 0 && " You can still apply what was written before it stopped."]
					}),
					!generating && review.fields.length === 0 && review.status !== "failed" && /* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						color: "secondary",
						className: "py-4",
						children: "Nothing to fill in — every field either already has a value the model would not improve on, or is not one it can write."
					})
				]
			}),
			/* @__PURE__ */ jsxs(DialogActions, { children: [/* @__PURE__ */ jsx(Button, {
				variant: "text",
				color: "neutral",
				onClick: controller.dismissReview,
				children: "Discard"
			}), /* @__PURE__ */ jsx(Button, {
				variant: "filled",
				disabled: applicable.length === 0,
				onClick: controller.applyReview,
				children: applicable.length === 1 ? "Apply 1 field" : `Apply ${applicable.length} fields`
			})] })
		]
	});
}
function ProposedFieldRow({ field, onToggle }) {
	const replaces = hasValue(field.currentValue) && !isSameValue(field.currentValue, field.proposed);
	return /* @__PURE__ */ jsxs("label", {
		className: cls("flex items-start gap-3 py-3 cursor-pointer", !field.selected && "opacity-50"),
		children: [/* @__PURE__ */ jsx("div", {
			className: "mt-0.5 shrink-0",
			children: /* @__PURE__ */ jsx(Checkbox, {
				checked: field.selected,
				size: "small",
				onCheckedChange: onToggle
			})
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex flex-col gap-1 min-w-0 grow",
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: "flex items-center gap-2",
					children: [
						/* @__PURE__ */ jsx(Typography, {
							variant: "label",
							component: "span",
							children: field.label
						}),
						replaces && /* @__PURE__ */ jsx(Typography, {
							variant: "caption",
							color: "secondary",
							children: "replaces the current value"
						}),
						field.pending && /* @__PURE__ */ jsx(CircularProgress, { size: "smallest" })
					]
				}),
				replaces && /* @__PURE__ */ jsx(Typography, {
					variant: "body2",
					color: "secondary",
					className: "line-through whitespace-pre-wrap break-words",
					children: renderValue(field.currentValue)
				}),
				/* @__PURE__ */ jsx(Typography, {
					variant: "body2",
					className: "whitespace-pre-wrap break-words",
					children: renderValue(field.proposed)
				})
			]
		})]
	});
}
function hasValue(value) {
	if (value === null || value === void 0) return false;
	if (typeof value === "string") return value.trim().length > 0;
	if (Array.isArray(value)) return value.length > 0;
	return true;
}
function isSameValue(a, b) {
	if (a === b) return true;
	if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
	if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => isSameValue(v, b[i]));
	return false;
}
/** Values are shown, never edited here — so a readable string is all that is needed. */
function renderValue(value) {
	if (value === null || value === void 0) return "";
	if (value instanceof Date) return value.toLocaleString();
	if (Array.isArray(value)) return value.map((v) => renderValue(v)).join(", ");
	if (typeof value === "boolean") return value ? "Yes" : "No";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
}
//#endregion
//#region src/components/FormEnhanceAction.tsx
function FormEnhanceAction({ path, status, collection, formContext }) {
	const storageKey = createLocalStorageKey(path, status);
	const dataEnhancementController = useDataEnhancementController();
	const [samplePrompts, setSamplePrompts] = React.useState(void 0);
	const [instructions, setInstructions] = React.useState("");
	const getSamplePrompts = dataEnhancementController?.getSamplePrompts;
	/**
	* Driven by the controller rather than by local state.
	*
	* There is exactly one run at a time, and the review owns it — a second
	* `loading` flag here could disagree with the dialog about whether the
	* model is still writing.
	*/
	const loading = dataEnhancementController?.review?.status === "generating";
	const loadingPrompts = useRef(false);
	const updateSuggestedPrompts = useCallback(async function updateSuggestedPrompts(instructions) {
		if (!getSamplePrompts) return;
		if (loadingPrompts.current) return;
		loadingPrompts.current = true;
		const prompts = status === "new" ? (await getSamplePrompts(collection.singularName ?? collection.name, instructions)).prompts : getPromptsForExistingEntities(collection.properties);
		const recentPromptsFromStorage = getRecentPromptsFromStorage(storageKey);
		const recentPrompts = recentPromptsFromStorage.map((prompt) => prompt.prompt);
		setSamplePrompts([...recentPromptsFromStorage, ...prompts.filter((p) => !recentPrompts.includes(p.prompt))].slice(0, 5));
		loadingPrompts.current = false;
	}, [
		collection.name,
		collection.singularName,
		getSamplePrompts,
		status
	]);
	useEffect(() => {
		if (!dataEnhancementController) return;
		if (!samplePrompts) {
			setSamplePrompts(getRecentPromptsFromStorage(storageKey));
			updateSuggestedPrompts().then();
		}
	}, [
		dataEnhancementController,
		samplePrompts,
		storageKey,
		updateSuggestedPrompts,
		instructions,
		status
	]);
	useEffect(() => {
		if (!dataEnhancementController) return;
		updateSuggestedPrompts().then();
	}, [dataEnhancementController, status]);
	/**
	* Starts a run and opens the review. Nothing is written to the form here —
	* see {@link AutofillReviewDialog}.
	*/
	const generate = (prompt) => {
		if (!dataEnhancementController || !formContext?.values) return;
		if (prompt) {
			addRecentPrompt(storageKey, prompt);
			setSamplePrompts([{
				prompt,
				type: "recent"
			}, ...(samplePrompts ?? []).slice(0, 5)]);
		}
		dataEnhancementController.generate({
			values: formContext.values,
			instructions: prompt
		}).catch(() => void 0);
	};
	if (!dataEnhancementController?.enabled) return null;
	function submit() {
		generate(instructions);
	}
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs(Menu, {
		align: "end",
		sideOffset: 8,
		className: "max-w-[100vw]",
		trigger: /* @__PURE__ */ jsxs(IconButton, {
			variant: "filled",
			size: "small",
			"aria-label": "Autofill",
			title: "Autofill",
			disabled: loading,
			children: [!loading && /* @__PURE__ */ jsx(AIIcon, { size: "small" }), loading && /* @__PURE__ */ jsx(CircularProgress, { size: "small" })]
		}),
		children: [
			/* @__PURE__ */ jsxs(MenuItem, {
				className: "py-4",
				onClick: () => {
					generate();
				},
				children: [/* @__PURE__ */ jsx(AIIcon, { size: "small" }), "Autofill based on the current content"]
			}),
			/* @__PURE__ */ jsx(Separator, {
				orientation: "horizontal",
				className: "mt-2"
			}),
			samplePrompts?.map((samplePrompt, index) => {
				return /* @__PURE__ */ jsxs(MenuItem, {
					onClick: () => {
						setInstructions(samplePrompt.prompt);
						generate(samplePrompt.prompt);
					},
					children: [/* @__PURE__ */ jsx("div", {
						className: "pl-9 grow text-text-secondary dark:text-text-secondary-dark",
						children: samplePrompt.prompt
					}), samplePrompt.type === "recent" && /* @__PURE__ */ jsx(IconButton, {
						onClick: (e) => {
							e.preventDefault();
							e.stopPropagation();
							removeRecentPrompt(storageKey, samplePrompt.prompt);
							setSamplePrompts((samplePrompts ?? []).filter((p) => p.prompt !== samplePrompt.prompt));
						},
						size: "smallest",
						children: /* @__PURE__ */ jsx(XIcon, { size: iconSize.smallest })
					})]
				}, index + "_" + samplePrompt.prompt);
			}),
			/* @__PURE__ */ jsx(Separator, { orientation: "horizontal" }),
			/* @__PURE__ */ jsxs("div", {
				className: cls("my-2 px-4 py-2 gap-4 w-[500px] max-w-full flex items-center text-surface-700 dark:text-surface-200"),
				children: [/* @__PURE__ */ jsxs("div", {
					className: "relative w-full grow",
					children: [/* @__PURE__ */ jsx(TextareaAutosize, {
						className: cls("p-3 pr-12 rounded-lg resize-none w-full outline-hidden max-h-[300px] overflow-auto", fieldBackgroundMixin, focusedDisabled),
						value: instructions,
						autoFocus: status === "new",
						disabled: loading,
						onFocus: (event) => {
							event.stopPropagation();
						},
						placeholder: "...or provide instructions",
						onKeyDown: (e) => {
							e.stopPropagation();
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								submit();
							}
						},
						onChange: (e) => {
							setInstructions(e.target.value);
						}
					}), instructions.length > 0 && !loading && /* @__PURE__ */ jsx("div", {
						className: "flex flex-row justify-center items-center absolute h-full right-0 top-0 mr-2",
						children: /* @__PURE__ */ jsx(IconButton, {
							size: "small",
							onClick: () => {
								setInstructions("");
							},
							children: /* @__PURE__ */ jsx(XIcon, { size: iconSize.small })
						})
					})]
				}), /* @__PURE__ */ jsxs(IconButton, {
					onClick: () => generate(instructions),
					size: "small",
					color: !instructions ? "primary" : void 0,
					disabled: loading || !instructions,
					children: [loading && /* @__PURE__ */ jsx(CircularProgress, { size: "smallest" }), !loading && /* @__PURE__ */ jsx(SendIcon, { size: iconSize.small })]
				})]
			})
		]
	}), /* @__PURE__ */ jsx(AutofillReviewDialog, {})] });
}
function getPromptsForExistingEntities(properties) {
	const multilineProperties = Object.values(properties).filter((p) => {
		if (isPropertyBuilder(p)) return false;
		return p.type === "string" && (p.admin?.markdown || p.admin?.multiline);
	});
	const multilinePrompt = multilineProperties.length > 0 ? multilineProperties[Math.floor(Math.random() * multilineProperties.length)] : void 0;
	const prompts = ["Fill the missing fields", "Translate the missing content"];
	if (multilinePrompt) prompts.push(`Add 2 paragraphs to '${multilinePrompt.name}'`);
	return prompts.map((p) => ({
		prompt: p,
		type: "sample"
	}));
}
var createLocalStorageKey = (path, status) => {
	return `data_enhancement::${status === "new" ? "new" : "existing"}::${stripCollectionPath(path)}`;
};
var getRecentPromptsFromStorage = (storageKey) => {
	const item = localStorage.getItem(storageKey);
	return item ? JSON.parse(item).map((e) => ({
		prompt: e,
		type: "recent"
	})) : [];
};
var addRecentPrompt = (storageKey, prompt) => {
	if (!prompt || prompt.trim().length === 0) return;
	const recentPrompts = getRecentPromptsFromStorage(storageKey);
	localStorage.setItem(storageKey, JSON.stringify([prompt, ...recentPrompts.map((e) => e.prompt).filter((e) => e !== prompt).slice(0, 5)]));
};
var removeRecentPrompt = (storageKey, prompt) => {
	localStorage.setItem(storageKey, JSON.stringify(getRecentPromptsFromStorage(storageKey).map((e) => e.prompt).filter((e) => e !== prompt)));
};
//#endregion
//#region src/useDataEnhancementPlugin.tsx
/**
* Use this hook to initialise the data enhancement plugin.
* This is likely the only hook you will need to use.
* @param props
*/
function useDataEnhancementPlugin(props) {
	const getConfigForPath = props?.getConfigForPath;
	const endpoint = props?.endpoint;
	return React.useMemo(() => ({
		key: "data_enhancement",
		slots: [{
			slot: "form.actions",
			Component: FormEnhanceAction,
			order: 40
		}],
		providers: [{
			scope: "form",
			Component: DataEnhancementControllerProvider,
			props: {
				getConfigForPath,
				endpoint
			}
		}]
	}), [getConfigForPath, endpoint]);
}
//#endregion
export { useDataEnhancementPlugin, useEditorAIController };

//# sourceMappingURL=index.es.js.map